/**
 * @description
 * BankLetteringComponent provides an interactive interface for sales bank reconciliation
 * and receivable lettering (Étape 4). It enables operators to record an incoming bank
 * statement line (date, method, bank reference, total received) and allocate amounts across
 * one or multiple open customer/third-party receivables with real-time balancing controls
 * and BOFIP cryptographic sealing.
 *
 * Called via route: `/sales/bank-lettering`
 *
 * Dependencies:
 * - BankLetteringService (`@app/sales/bank-lettering/bank-lettering.service`)
 * - LoginService (`@app/common/login.service`) : poste courant, dont dépend le poste d'arrivée de chaque mode
 * - AlertService (`@app/shared/alert.service`)
 * - SpinnerService (`@app/shared/spinner.service`)
 * - SearchPartnerComponent (`@app/shared/search-partner/search-partner.component`)
 * - SelectDateComponent (`@app/shared/select-date/select-date.component`)
 * - MenuCommonComponent (`@app/shared/menu-common/menu-common.component`)
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { catchError, finalize, of, switchMap } from 'rxjs';
import Swal from 'sweetalert2';

import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SearchPartnerComponent } from '@app/shared/search-partner/search-partner.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { SpinnerService } from '@app/shared/spinner.service';
import { Partner } from '@app/shared/_models/common';
import { BankLetteringAllocation, BankLetteringPayload, BankLetteringResult, LetteringMethodRoute, LetteringRouting, OpenReceivable, OpenReceivablesQueryParams } from '@app/shared/_models/sales';

import { BankLetteringService } from './bank-lettering.service';

// Texte saisi injecté dans le HTML de la boîte : échappé, jamais interprété.
const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/**
 * Filter configuration for open receivables list.
 */
export interface ReceivablesFilter {
  /** Filter by payer partner */
  payerId: number | null;
  /** Include third-party healthcare payers (AMO / AMC) */
  includeTp: boolean;
  /** Search query across docRef, order, customer */
  search: string;
  /** Result page size */
  limit: number;
}

/**
 * Bank settlement header form model.
 */
export interface BankStatementForm {
  /** Date of bank settlement */
  dateReglement: Date;
  /** Payment method ID (c_values 7) ; null tant que le serveur n'a pas dit quels modes ce poste accepte */
  paymentMethod: number | null;
  /** Compte crédité d'un virement (poste IS_BANK) */
  bankStation: number | null;
  /** Bank account / IBAN label */
  bankAccount: string;
  /** Bank statement reference / transfer ID */
  bankReference: string;
  /** Total received amount in € */
  totalAmount: number | null;
  /** Internal note / memo */
  note: string;
}

@Component({
  selector: 'app-sales-bank-lettering',
  standalone: true,
  templateUrl: './bank-lettering.component.html',
  styleUrls: ['./bank-lettering.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, RouterModule, DatePipe, DecimalPipe, MatButtonModule, MatCheckboxModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatProgressSpinnerModule, MatTooltipModule, MatDividerModule, MatCardModule, MenuCommonComponent, SearchPartnerComponent, SelectDateComponent],
})
export class BankLetteringComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private letteringService = inject(BankLetteringService);
  private alertService = inject(AlertService);
  private spinner = inject(SpinnerService);
  private loginService = inject(LoginService);

  /** List of fetched open receivables */
  public receivables: OpenReceivable[] = [];

  /** Modes lettrables et leur poste d'arrivée, lus pour le poste courant */
  public routing: LetteringRouting | null = null;
  public routingFailed = false;

  /** Loading state indicator */
  public isLoading: boolean = false;

  /** Submitting state indicator */
  public isSubmitting: boolean = false;

  /** Selected partner for filtering */
  public selectedPartner: Partner | null = null;

  /** Receivables filter model */
  public filter: ReceivablesFilter = {
    payerId: null,
    includeTp: false,
    search: '',
    limit: 50,
  };

  /** Bank settlement header form */
  public statement: BankStatementForm = {
    dateReglement: new Date(),
    paymentMethod: null,
    bankStation: null,
    bankAccount: '',
    bankReference: '',
    totalAmount: null,
    note: '',
  };

  /** Map of allocated amounts keyed by receivable ID */
  public allocatedAmounts: Map<number, number> = new Map();

  /** Set of selected receivable IDs */
  public selectedReceivableIds: Set<number> = new Set();

  /** Available limit options */
  public readonly limitOptions: number[] = [25, 50, 100, 200];

  ngOnInit(): void {
    this.alertService.clear();
    this.loadRouting();
    this.loadOpenReceivables(); // Run on auto
  }

  // Relu à chaque changement de poste : les refus dépendent du poste courant (en-tête X-Station-Id).
  private loadRouting(): void {
    this.loginService.station$
      .pipe(
        switchMap(() => this.letteringService.getRouting$().pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(r => {
        this.routing = r;
        this.routingFailed = r === null;
        this.pickDefaults();
        this.cdr.markForCheck();
      });
  }

  // Défaut visible, jamais par l'id ni le nom : premier virement accepté, sinon premier mode accepté ; compte = l'unique.
  private pickDefaults(): void {
    const ms = this.routing?.methods ?? [];
    if (!ms.some(m => m.id === this.statement.paymentMethod && !m.refusal)) this.statement.paymentMethod = (ms.find(m => m.arrival === 'BANK' && !m.refusal) ?? ms.find(m => !m.refusal))?.id ?? null;
    const banks = this.routing?.bank_stations ?? [];
    if (!banks.some(b => b.id === this.statement.bankStation)) this.statement.bankStation = banks.length === 1 ? banks[0].id : null;
  }

  get method(): LetteringMethodRoute | undefined {
    return this.routing?.methods.find(m => m.id === this.statement.paymentMethod);
  }
  get isBankMethod(): boolean {
    return this.method?.arrival === 'BANK';
  }

  get routingRefusal(): string | null {
    if (this.routingFailed) return "Poste d'arrivée inconnu : rechargez l'écran";
    const m = this.method;
    if (!m) {
      const all = this.routing?.methods ?? [];
      // Tous refusés (poste inconnu, sans session…) : le motif du serveur, pas une invite à choisir une option grisée.
      return all.length > 0 && all.every(x => !!x.refusal) ? all[0].refusal : 'Choisissez un mode de règlement';
    }
    if (m.refusal) return m.refusal;
    return this.isBankMethod && this.statement.bankStation === null ? 'Choisissez le compte crédité' : null;
  }

  get targetLabel(): string {
    if (this.isBankMethod) return this.routing?.bank_stations.find(b => b.id === this.statement.bankStation)?.name ?? 'Compte à choisir';
    return this.method?.station_name ?? this.routing?.till?.name ?? 'Aucun poste';
  }

  // Ce que l'enregistrement change pour les caisses : un virement n'entre ni au tiroir ni au Z d'un magasin.
  get targetHint(): string {
    return this.isBankMethod ? 'reçu sur le compte bancaire, hors tiroir et hors Z des caisses' : 'entre dans la session et le Z de ce poste';
  }

  // La référence suit le mode : n° de relevé, banque tirée du chèque (clé du bordereau de remise), ticket TPE.
  get referenceLabel(): string {
    switch (this.method?.arrival) {
      case 'DESK':
        return 'Banque tirée et n° du chèque';
      case 'TERMINAL':
        return 'N° de ticket TPE';
      case 'DRAWER':
        return 'Commentaire';
      default:
        return 'Référence du relevé / virement';
    }
  }

  /**
   * Loads open receivables from the backend matching active filter criteria.
   */
  public loadOpenReceivables(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    const queryParams: OpenReceivablesQueryParams = {
      payerId: this.filter.payerId,
      includeTp: this.filter.includeTp,
      search: this.filter.search ? this.filter.search.trim() : null,
      limit: this.filter.limit,
      offset: 0,
    };

    this.letteringService
      .getOpenReceivables(queryParams)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(err => {
          const serverMsg = err.error?.message || err.error || err.message || 'Erreur serveur inconnue';
          Swal.fire({
            title: 'Erreur',
            text: `Impossible de charger les créances ouvertes.\nDétails : ${serverMsg}`,
            icon: 'error',
            confirmButtonText: 'Fermer',
          });
          return of([] as OpenReceivable[]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe((data: OpenReceivable[]) => {
        this.receivables = data || [];
        // Retain only allocations for still-visible receivables
        const validIds = new Set(this.receivables.map(r => r.id));
        for (const id of Array.from(this.selectedReceivableIds)) {
          if (!validIds.has(id)) {
            this.selectedReceivableIds.delete(id);
            this.allocatedAmounts.delete(id);
          }
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Handles partner selection change.
   *
   * @param partner Selected partner or null.
   */
  public onPartnerChange(partner: Partner | null): void {
    this.selectedPartner = partner;
    this.filter.payerId = partner && partner.id > 0 ? partner.id : null;
    this.loadOpenReceivables();
  }

  /**
   * Triggers a new receivables search.
   */
  public onSearch(): void {
    this.loadOpenReceivables();
  }

  /**
   * Resets receivables search filters.
   */
  public onResetFilters(): void {
    this.selectedPartner = null;
    this.filter = {
      payerId: null,
      includeTp: false,
      search: '',
      limit: 50,
    };
    this.loadOpenReceivables();
  }

  /**
   * Handles date selection change for settlement date.
   *
   * @param date Selected date.
   */
  public onDateChange(date: Date): void {
    this.statement.dateReglement = date;
  }

  /**
   * Checks whether a receivable can be selected for bank lettering.
   * Only invoiced receivables (with docRef) can be lettered.
   * Orders pending delivery ("En attente de livraison") and delivered but not yet invoiced ("Livré non facturé") cannot be lettered.
   *
   * @param rec Target OpenReceivable.
   * @returns True if the receivable is invoiced (has docRef).
   */
  public isReceivableSelectable(rec: OpenReceivable): boolean {
    return !!rec.docRef;
  }

  /**
   * Toggles selection of a specific receivable row.
   *
   * @param rec Target OpenReceivable.
   */
  public toggleReceivableSelection(rec: OpenReceivable): void {
    if (!this.isReceivableSelectable(rec)) {
      return;
    }
    if (this.selectedReceivableIds.has(rec.id)) {
      this.selectedReceivableIds.delete(rec.id);
      this.allocatedAmounts.delete(rec.id);
    } else {
      this.selectedReceivableIds.add(rec.id);
      // Auto-suggest allocation amount
      const currentAllocated = this.getTotalAllocated();
      const declaredTotal = Number(this.statement.totalAmount || 0);
      const remainingBank = Number((declaredTotal - currentAllocated).toFixed(2));

      let suggestedAmount = rec.amountDue;
      if (rec.amountDue > 0) {
        if (remainingBank > 0 && remainingBank < rec.amountDue) {
          suggestedAmount = remainingBank;
        }
      } else {
        if (remainingBank < 0 && remainingBank > rec.amountDue) {
          suggestedAmount = remainingBank;
        }
      }
      this.allocatedAmounts.set(rec.id, Number(suggestedAmount.toFixed(2)));
    }
    this.cdr.markForCheck();
  }

  /**
   * Checks if a receivable is selected.
   *
   * @param recId Receivable ID.
   * @returns True if selected.
   */
  public isReceivableSelected(recId: number): boolean {
    return this.selectedReceivableIds.has(recId);
  }

  /**
   * Selects all visible open receivables that are selectable and auto-populates their full due amounts.
   */
  public selectAllReceivables(): void {
    for (const rec of this.receivables) {
      if (!this.isReceivableSelectable(rec)) {
        continue;
      }
      this.selectedReceivableIds.add(rec.id);
      if (!this.allocatedAmounts.has(rec.id) || this.allocatedAmounts.get(rec.id) === 0) {
        this.allocatedAmounts.set(rec.id, rec.amountDue);
      }
    }
    this.cdr.markForCheck();
  }

  /**
   * Clears all selected receivables and allocations.
   */
  public deselectAllReceivables(): void {
    this.selectedReceivableIds.clear();
    this.allocatedAmounts.clear();
    this.cdr.markForCheck();
  }

  /**
   * Sets the allocated amount of a receivable to its exact full due balance.
   *
   * @param rec Target OpenReceivable.
   */
  public setFullBalance(rec: OpenReceivable): void {
    if (!this.isReceivableSelectable(rec)) {
      return;
    }
    this.selectedReceivableIds.add(rec.id);
    this.allocatedAmounts.set(rec.id, rec.amountDue);
    this.cdr.markForCheck();
  }

  /**
   * Sets the allocated amount to the remaining bank funds.
   *
   * @param rec Target OpenReceivable.
   */
  public setRemainingBankAmount(rec: OpenReceivable): void {
    if (!this.isReceivableSelectable(rec)) {
      return;
    }
    this.selectedReceivableIds.add(rec.id);
    const declaredTotal = Number(this.statement.totalAmount || 0);
    const currentAllocatedExcludingThis = this.getTotalAllocated() - (this.allocatedAmounts.get(rec.id) || 0);
    const remainingBank = Number((declaredTotal - currentAllocatedExcludingThis).toFixed(2));
    let targetAmount = rec.amountDue;
    if (rec.amountDue > 0) {
      targetAmount = remainingBank > 0 ? Math.min(remainingBank, rec.amountDue) : 0;
    } else {
      targetAmount = remainingBank < 0 ? Math.max(remainingBank, rec.amountDue) : 0;
    }
    if (targetAmount !== 0) {
      this.allocatedAmounts.set(rec.id, targetAmount);
    }
    this.cdr.markForCheck();
  }

  /**
   * Updates allocation amount from user text/number input.
   *
   * @param rec Target OpenReceivable.
   * @param value Raw input value.
   */
  public onAllocationChange(rec: OpenReceivable, value: any): void {
    if (!this.isReceivableSelectable(rec)) {
      return;
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      this.allocatedAmounts.set(rec.id, 0);
    } else {
      this.allocatedAmounts.set(rec.id, Number(num.toFixed(2)));
    }
    if (!this.selectedReceivableIds.has(rec.id) && num !== 0) {
      this.selectedReceivableIds.add(rec.id);
    }
    this.cdr.markForCheck();
  }

  /**
   * Gets current allocated amount for a receivable.
   *
   * @param recId Receivable ID.
   * @returns Allocated amount in €.
   */
  public getAllocatedAmount(recId: number): number {
    return this.allocatedAmounts.get(recId) || 0;
  }

  /**
   * Checks if an allocation has validation error (exceeds balance or non-positive).
   *
   * @param rec Target OpenReceivable.
   * @returns True if invalid.
   */
  public isAllocationInvalid(rec: OpenReceivable): boolean {
    if (!this.selectedReceivableIds.has(rec.id)) return false;
    const alloc = this.getAllocatedAmount(rec.id);
    if (alloc === 0) return true;
    if (rec.amountDue > 0) {
      return alloc < 0 || alloc > rec.amountDue;
    } else {
      return alloc > 0 || alloc < rec.amountDue;
    }
  }

  /**
   * Computes the sum of all allocated amounts.
   *
   * @returns Total allocated amount in €.
   */
  public getTotalAllocated(): number {
    let sum = 0;
    for (const recId of Array.from(this.selectedReceivableIds)) {
      sum += this.allocatedAmounts.get(recId) || 0;
    }
    return Number(sum.toFixed(2));
  }

  /**
   * Computes difference between declared bank total and sum of allocations.
   *
   * @returns Difference in € (0.00 = balanced).
   */
  public getBalanceDifference(): number {
    const bankTotal = Number(this.statement.totalAmount || 0);
    const allocated = this.getTotalAllocated();
    return Number((bankTotal - allocated).toFixed(2));
  }

  /**
   * Returns whether the reconciliation is balanced and ready to validate.
   *
   * @returns True if balanced.
   */
  public isBalanced(): boolean {
    const bankTotal = Number(this.statement.totalAmount || 0);
    const diff = Math.abs(this.getBalanceDifference());
    const count = this.selectedReceivableIds.size;
    if (count === 0 || diff > 0.005) {
      return false;
    }
    for (const rec of this.receivables) {
      if (this.selectedReceivableIds.has(rec.id)) {
        if (this.isAllocationInvalid(rec)) return false;
      }
    }
    return true;
  }

  /**
   * Auto-fills the declared bank amount from the sum of selected allocations.
   */
  public autofillBankAmountFromAllocations(): void {
    const total = this.getTotalAllocated();
    if (total !== 0) {
      this.statement.totalAmount = total;
      this.cdr.markForCheck();
    }
  }

  /**
   * Validates and submits the bank lettering operation.
   */
  public onSubmitLettering(): void {
    // Filet : une option désactivée reste choisissable en JS (harnais NRT, choisir_option).
    if (this.routingRefusal) {
      Swal.fire({ title: 'Mode de règlement refusé', text: this.routingRefusal, icon: 'warning' });
      return;
    }
    if (!this.isBalanced()) return;

    const allocations: BankLetteringAllocation[] = [];
    for (const recId of Array.from(this.selectedReceivableIds)) {
      const amount = this.allocatedAmounts.get(recId) || 0;
      if (amount !== 0) {
        allocations.push({
          receivable_id: recId,
          amount: amount,
        });
      }
    }

    if (allocations.length === 0) {
      Swal.fire({
        title: 'Aucune imputation',
        text: 'Veuillez sélectionner au moins une créance et saisir un montant à imputer.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    const tpAllocationsCount = allocations.filter(a => {
      const rec = this.receivables.find(r => r.id === a.receivable_id);
      return rec && (rec.payerType === 'AMO' || rec.payerType === 'AMC');
    }).length;

    const tpWarningHtml =
      tpAllocationsCount > 0
        ? `
      <div style="margin-top: 10px; padding: 8px 10px; background: #fffbeb; border-left: 4px solid #f59e0b; border-radius: 4px; color: #92400e; font-size: 12.5px; text-align: left;">
        ⚠️ <strong>Attention Tiers-Payeur (${tpAllocationsCount} créance${tpAllocationsCount > 1 ? 's' : ''} AMO/AMC)</strong> : Ces créances relèvent de la télétransmission FSE et sont normalement lettrées via les retours NOEMIE. Validez uniquement pour un règlement manuel d'exception.
      </div>
    `
        : '';

    const methodName = this.method?.name ?? '';
    const totalStr = (this.statement.totalAmount || 0).toFixed(2);

    Swal.fire({
      title: 'Confirmer le lettrage bancaire',
      html: `
        <div style="text-align: left; font-size: 14px; line-height: 1.6;">
          <div><span>Montant total : </span><span>${totalStr} €</span></div>
          <div><span>Mode de règlement : </span><span>${esc(methodName)}</span></div>
          <div><span>Enregistré sur : </span><span>${esc(this.targetLabel)}</span> <span style="color: #64748b;">(${this.targetHint})</span></div>
          <div><span>${esc(this.referenceLabel)} : </span><span>${esc(this.statement.bankReference || 'Non renseignée')}</span></div>
          <div><span>Nombre de créances : </span><span>${allocations.length}</span></div>
          ${tpWarningHtml}
          <div style="margin-top: 10px; padding: 8px; background: #f0fdf4; border-radius: 6px; color: #166534; font-size: 12.5px;">
            🔒 Cette opération est définitive et générera un scellement fiscal BOFIP (safe.payment).
          </div>
        </div>
      `,
      icon: tpAllocationsCount > 0 ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Valider et sceller',
      cancelButtonText: 'Annuler',
    }).then(result => {
      if (result.isConfirmed) {
        this.executeLettering(allocations);
      }
    });
  }

  /**
   * Dispatches the lettering transaction to the backend.
   *
   * @param allocations List of allocations to apply.
   */
  private executeLettering(allocations: BankLetteringAllocation[]): void {
    this.isSubmitting = true;
    this.spinner.show();
    this.cdr.markForCheck();

    const payload: BankLetteringPayload = {
      // Le poste physique passe par X-Station-Id ; le corps ne porte le compte et la date que pour un virement.
      date_reglement: this.isBankMethod ? this.statement.dateReglement.toISOString() : null,
      payment_method: Number(this.statement.paymentMethod),
      bank_station: this.isBankMethod ? this.statement.bankStation : null,
      bank_account: this.isBankMethod && this.statement.bankAccount ? this.statement.bankAccount.trim() : null,
      bank_reference: this.statement.bankReference ? this.statement.bankReference.trim() : null,
      total_amount: this.statement.totalAmount,
      customer: this.filter.payerId,
      note: this.statement.note ? this.statement.note.trim() : null,
      allocations: allocations,
    };

    this.letteringService
      .submitBankLettering(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(err => {
          const serverMsg = err.error?.message || err.error || err.message || 'Erreur lors du traitement';
          Swal.fire({
            title: 'Échec du lettrage',
            text: `Le rapprochement bancaire a été rejeté par le serveur.\n\nMotif : ${serverMsg}`,
            icon: 'error',
            confirmButtonText: 'Fermer',
          });
          return of(null);
        }),
        finalize(() => {
          this.isSubmitting = false;
          this.spinner.hide();
          this.cdr.markForCheck();
        }),
      )
      .subscribe(res => {
        if (res && res.success) {
          const resultData: BankLetteringResult = res.data;
          Swal.fire({
            title: 'Lettrage validé avec succès !',
            html: `
              <div style="text-align: left; font-size: 13.5px; line-height: 1.6;">
                <div>Paiement enregistré : <span>#${resultData?.payment_id || ''}</span></div>
                ${resultData?.station_id ? `<div>Poste : <span>${esc(resultData.station_name)} (#${resultData.station_id})</span></div>` : ''}
                <div>Montant scellé BOFIP : <span>${(resultData?.total_amount || payload.total_amount || 0).toFixed(2)} €</span></div>
                <div>Commandes soldées : <span>${resultData?.fully_paid_orders?.length || 0}</span></div>
                <div style="font-size: 11px; color: #64748b; margin-top: 8px; word-break: break-all;">
                  Sceau cryptographique : ${resultData?.safe_payment_id || 'OK'}
                </div>
              </div>
            `,
            icon: 'success',
            confirmButtonText: 'OK',
          });

          // Reset selection & bank statement header form, reload data
          this.statement.totalAmount = null;
          this.statement.bankReference = '';
          this.statement.note = '';
          this.deselectAllReceivables();
          this.loadOpenReceivables();
        }
      });
  }

  /**
   * Returns badge styling for payer category.
   *
   * @param type Payer type string.
   * @returns Badge CSS class.
   */
  public getPayerTypeBadgeClass(type: string): string {
    switch ((type || '').toUpperCase()) {
      case 'AMO':
        return 'badge-amo';
      case 'AMC':
        return 'badge-amc';
      case 'CLIENT':
      default:
        return 'badge-client';
    }
  }

  /**
   * Returns a friendly explanatory label when docRef is not yet generated.
   *
   * @param rec OpenReceivable item.
   * @returns "Livré non facturé" if delivered, or "En attente de livraison".
   */
  public getUninvoicedLabel(rec: OpenReceivable): string {
    const state = (rec.orderState || '').toUpperCase();
    if (state === 'DELIVERED' || (rec.deliveryId !== null && rec.deliveryId !== undefined && rec.deliveryId > 0)) {
      return 'Livré non facturé';
    }
    return 'En attente de livraison';
  }

  /**
   * TrackBy function for open receivables list rendering optimization.
   *
   * @param index Item index.
   * @param item OpenReceivable item.
   * @returns Unique receivable ID.
   */
  public trackByReceivable(index: number, item: OpenReceivable): number {
    return item.id;
  }
}
